/**
 * ImplementationStatusPanel — Iter 167.f
 *
 * Customer Card panel that drives the Implementation tracker per
 * Danny's Customers Section Overhaul spec (Part 4). Shows track
 * stages, kickoff date, estimated go-live, assigned Onboarding
 * Specialist, and offers stage-advance / specialist-assignment
 * controls (admin/manager only).
 *
 * Two tracks supported:
 *   - EHR/PMS  (7 stages — clamped to 1..7 by the backend)
 *   - Care Coord (6 stages — clamped to 1..6)
 * A customer may be enrolled in EITHER, BOTH (hybrid), or NEITHER
 * (in which case this panel renders an "Add an enrollment" hint
 * pointing to the existing ContactCard enrollment editor below).
 *
 * Data shape comes from GET /api/customers/{id}/bucket which returns:
 *   { bucket, tracks: [...], track_label, implementation: {...},
 *     enrolled_products: [...], days_since_kickoff }
 *
 * Mutations go through PUT /api/customers/{id}/implementation (admin
 * only; backend clamps stage ints and auto-flips bucket=live +
 * account_status=active when all in-scope tracks hit their final stage).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { ChevronRight, CheckCircle2, Loader2 } from "lucide-react";

// Per-spec stage definitions. Order = stage number (1-indexed).
// Keeping these in the frontend rather than fetching from the backend
// keeps the panel snappy; the backend is the source of truth for
// validity (it clamps to these bounds on every write).
const EHR_PMS_STAGES = [
  "Kickoff scheduled",
  "Data migration",
  "User accounts provisioned",
  "EHR training (clinicians)",
  "PMS training (front-desk + billing)",
  "Parallel run / staging review",
  "Go-live",
];

const CARE_COORD_STAGES = [
  "Patient roster received",
  "Patient consent collected",
  "Initial outreach + onboarding calls",
  "Care plan documentation",
  "First billing cycle",
  "Steady-state monitoring (live)",
];

export default function ImplementationStatusPanel({ customerId, isAdmin = false }) {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialists, setSpecialists] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/customers/${customerId}/bucket`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(r.data);
    } catch (e) {
      // Non-fatal — panel just won't render. Don't toast here; the
      // parent ContactFocusView already shows a generic error if the
      // contact itself fails to load.
    } finally {
      setLoading(false);
    }
  }, [customerId, token]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  // Lazy-load the assignable-users list only when an admin opens the
  // specialist picker (avoids the request for non-admin viewers).
  const loadSpecialists = useCallback(async () => {
    if (specialists.length || !isAdmin) return;
    try {
      const r = await axios.get(
        `${API}/dialer/assignable-users`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSpecialists(r.data || []);
    } catch (_) { /* non-fatal */ }
  }, [specialists.length, isAdmin, token]);

  const update = async (patch) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/customers/${customerId}/implementation`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Implementation updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: "var(--surface)", borderRadius: 10, border: "0.5px solid var(--border)", padding: "14px 18px", display: "flex", alignItems: "center", gap: 8 }}>
        <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
        <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Loading implementation status…</span>
      </div>
    );
  }
  if (!data) return null;

  const isLive = data.bucket === "live";
  const tracks = data.tracks || [];
  const impl = data.implementation || {};
  const ehrStage = impl.ehr_pms_stage || 0;
  const ccStage = impl.care_coord_stage || 0;
  const specialistName = (() => {
    const sid = impl.specialist_user_id;
    if (!sid) return null;
    const u = specialists.find(s => s.id === sid);
    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : sid;
  })();

  // Customer is fully live + has no in-progress tracks → just show the badge.
  // Iter 167.g — only show the Live badge if the customer was EXPLICITLY
  // tagged "live" (or migrated from the legacy contacts with no
  // enrollment data and no bucket=implementation tag). For customers
  // explicitly placed in the Implementation bucket but with no tracks
  // yet, fall through to the empty-tracks state with CTAs.
  if (isLive && tracks.length === 0) {
    return (
      <div data-testid="implementation-live-badge" style={{ background: "var(--green-light)", borderRadius: 10, border: "0.5px solid var(--green)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <CheckCircle2 style={{ width: 14, height: 14, color: "var(--green)" }} />
        <div style={{ fontSize: 12, color: "var(--green)", fontWeight: 600 }}>
          Live customer — all products in steady state
        </div>
      </div>
    );
  }

  return (
    <div data-testid="implementation-status-panel" style={{ background: "var(--surface)", borderRadius: 10, border: "0.5px solid var(--border)", padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600 }}>Implementation status</h3>
        <span
          data-testid="implementation-bucket-pill"
          style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 600,
            background: isLive ? "var(--green-light)" : "var(--blue-light)",
            color: isLive ? "var(--green)" : "var(--blue)" }}>
          {(data.track_label || "live").toUpperCase()}
        </span>
      </div>

      {/* Top meta strip — kickoff, estimated go-live, specialist, days */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14, fontSize: 11 }}>
        <MetaCell label="Kickoff" value={impl.kickoff_at ? formatDate(impl.kickoff_at) : "—"} />
        <MetaCell label="Est. go-live" value={impl.estimated_go_live ? formatDate(impl.estimated_go_live) : "—"} />
        <MetaCell label="Specialist" value={specialistName || "Unassigned"} />
        <MetaCell label="Days since kickoff" value={data.days_since_kickoff != null ? data.days_since_kickoff : "—"} />
      </div>

      {/* Per-track stage trackers */}
      {tracks.includes("ehr_pms") && (
        <StageTracker
          label="EHR / PMS"
          stages={EHR_PMS_STAGES}
          currentStage={ehrStage}
          maxStage={7}
          onAdvance={(next) => update({ ehr_pms_stage: next })}
          canEdit={isAdmin}
          saving={saving}
          dataTestId="track-ehr-pms"
        />
      )}
      {tracks.includes("care_coord") && (
        <StageTracker
          label="Care Coordination"
          stages={CARE_COORD_STAGES}
          currentStage={ccStage}
          maxStage={6}
          onAdvance={(next) => update({ care_coord_stage: next })}
          canEdit={isAdmin}
          saving={saving}
          dataTestId="track-care-coord"
        />
      )}

      {tracks.length === 0 && (
        <div data-testid="implementation-empty-tracks" style={{ padding: "14px 0" }}>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 12 }}>
            No products enrolled yet. Pick a track to activate the stage tracker, or add product enrollments below to auto-derive tracks.
          </div>
          {isAdmin && (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                data-testid="impl-activate-ehr-pms"
                disabled={saving}
                onClick={() => update({ ehr_pms_stage: 1, kickoff_at: impl.kickoff_at || new Date().toISOString() })}
                className="btn"
                style={{ fontSize: 11, padding: "6px 12px", background: "var(--blue-light)", color: "var(--blue)", border: "none", borderRadius: 6, fontWeight: 600 }}>
                Start EHR / PMS track
              </button>
              <button
                data-testid="impl-activate-care-coord"
                disabled={saving}
                onClick={() => update({ care_coord_stage: 1, kickoff_at: impl.kickoff_at || new Date().toISOString() })}
                className="btn"
                style={{ fontSize: 11, padding: "6px 12px", background: "var(--blue-light)", color: "var(--blue)", border: "none", borderRadius: 6, fontWeight: 600 }}>
                Start Care Coordination track
              </button>
            </div>
          )}
        </div>
      )}

      {/* Admin controls — kickoff/ETA/specialist editor */}
      {isAdmin && (
        <details
          style={{ marginTop: 14, fontSize: 12 }}
          onToggle={(e) => { if (e.target.open) loadSpecialists(); }}>
          <summary data-testid="implementation-admin-toggle" style={{ cursor: "pointer", color: "var(--blue)", fontWeight: 500 }}>
            Edit kickoff / ETA / specialist
          </summary>
          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <DateField
              label="Kickoff date"
              value={impl.kickoff_at ? impl.kickoff_at.slice(0, 10) : ""}
              onSave={(v) => update({ kickoff_at: v ? `${v}T00:00:00+00:00` : null })}
              testid="impl-edit-kickoff"
            />
            <DateField
              label="Estimated go-live"
              value={impl.estimated_go_live ? impl.estimated_go_live.slice(0, 10) : ""}
              onSave={(v) => update({ estimated_go_live: v ? `${v}T00:00:00+00:00` : null })}
              testid="impl-edit-go-live"
            />
            <div style={{ gridColumn: "1 / span 2" }}>
              <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 4 }}>
                Onboarding Specialist
              </label>
              <select
                data-testid="impl-edit-specialist"
                value={impl.specialist_user_id || ""}
                onChange={(e) => update({ specialist_user_id: e.target.value || null })}
                style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "inherit" }}>
                <option value="">— Unassigned —</option>
                {specialists.map(s => (
                  <option key={s.id} value={s.id}>
                    {`${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function StageTracker({ label, stages, currentStage, maxStage, onAdvance, canEdit, saving, dataTestId }) {
  const done = currentStage >= maxStage;
  return (
    <div data-testid={dataTestId} style={{ marginBottom: 14, padding: "10px 12px", background: "var(--bg)", borderRadius: 8, border: "0.5px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{label}</span>
        <span style={{ fontSize: 11, color: done ? "var(--green)" : "var(--text-tertiary)", fontWeight: 500 }}>
          {done ? "✓ Live" : `Stage ${currentStage || 0} of ${maxStage}`}
        </span>
      </div>
      {/* Stepper bar */}
      <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
        {stages.map((_, i) => (
          <div
            key={i}
            style={{ flex: 1, height: 5, borderRadius: 3,
              background: i < currentStage ? "var(--green)" : "var(--border)" }}
          />
        ))}
      </div>
      {/* Stage label list (compact) */}
      <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
        {currentStage > 0 && currentStage <= stages.length ? (
          <span><strong>{currentStage}.</strong> {stages[currentStage - 1]}</span>
        ) : (
          <span style={{ fontStyle: "italic", color: "var(--text-tertiary)" }}>Not started — set stage 1 to begin</span>
        )}
      </div>
      {/* Advance button */}
      {canEdit && !done && (
        <button
          data-testid={`${dataTestId}-advance`}
          disabled={saving}
          onClick={() => onAdvance(Math.min((currentStage || 0) + 1, maxStage))}
          className="btn"
          style={{ marginTop: 8, fontSize: 11, padding: "4px 10px",
            background: "var(--blue-light)", color: "var(--blue)", border: "none", borderRadius: 6,
            display: "inline-flex", alignItems: "center", gap: 4 }}>
          Advance to stage {(currentStage || 0) + 1}
          <ChevronRight style={{ width: 11, height: 11 }} />
        </button>
      )}
    </div>
  );
}

function DateField({ label, value, onSave, testid }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  return (
    <div>
      <label style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 4 }}>
        {label}
      </label>
      <input
        type="date"
        data-testid={testid}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onSave(local); }}
        style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "inherit" }}
      />
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_) { return iso; }
}
